import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";

import { DateCell } from "~/shared/components/trax-grid/date-cell/date-cell.tsx";
import { DateTextFilterCell } from "~/shared/components/trax-grid/date-text-filter-cell/date-text-filter-cell.tsx";
import { GridTd } from "~/shared/components/trax-grid/grid-td/grid-td.tsx";
import type { BoardColumn } from "~/shared/types/quote-board-types.ts";
import { formatCurrency } from "~/shared/utils/string-fns.ts";

const dateCell = DateCell;

const currencyCell = (props: GridCustomCellProps) => {
	const dataItem = props.dataItem as Record<string, unknown>;
	const value = dataItem[props.field ?? ""] as number | null;
	return (
		<GridTd tdProps={props.tdProps}>
			{value == null ? "" : formatCurrency(value)}
		</GridTd>
	);
};

const COUNTRY_CODE_MAP: Record<string, string> = { US: "USA", CA: "CAN" };

export const formatCountryCode = (code: string | null | undefined): string => {
	if (!code) return "";
	return COUNTRY_CODE_MAP[code] ?? code;
};

const countryCell = (props: GridCustomCellProps) => {
	const dataItem = props.dataItem as Record<string, unknown>;
	const value = dataItem[props.field ?? ""] as string | null;
	return <GridTd tdProps={props.tdProps}>{formatCountryCode(value)}</GridTd>;
};

export const formatTimeFromDateTime = (
	date: string | null | undefined,
): string => {
	if (!date) return "";
	const d = new Date(date);
	if (Number.isNaN(d.getTime())) return "";
	if (d.getFullYear() <= 1900) return "";
	return d.toLocaleTimeString("en-US", {
		hour: "2-digit",
		minute: "2-digit",
		hour12: true,
		timeZone: "UTC",
	});
};

const timeCell = (props: GridCustomCellProps) => {
	const dataItem = props.dataItem as Record<string, unknown>;
	const value = dataItem[props.field ?? ""] as string | null;
	return (
		<GridTd tdProps={props.tdProps}>{formatTimeFromDateTime(value)}</GridTd>
	);
};

/**
 * Minimum column width (px) for Shipment Detail columns. Many columns can be
 * toggled on via the column chooser; enforcing a readable minimum lets the
 * grid overflow horizontally so users get a scrollbar instead of squeezed,
 * illegible columns.
 */
const SHIPMENT_DETAIL_MIN_COLUMN_WIDTH = 150;

export const SHIPMENT_DETAIL_COLUMNS: BoardColumn[] = (
	[
		{
			field: "rateType",
			title: "Mode",
			show: true,
			editable: false,
		},
		{
			field: "customerPO",
			title: "Customer PO",
			show: true,
			editable: false,
		},
		{
			field: "poNumber",
			title: "TQL PO #",
			show: true,
			editable: false,
		},
		{
			field: "deliveryApptDateTime",
			title: "Scheduled Delivery Date",
			show: true,
			minWidth: 200,
			filterCell: DateTextFilterCell,
			cells: { data: dateCell },
		},
		{
			field: "scheduledDeliveryTime",
			title: "Scheduled Delivery Time",
			show: true,
			minWidth: 200,
			cells: { data: timeCell },
		},
		{
			field: "originCity",
			title: "Origin City",
			show: true,
			editable: false,
		},
		{
			field: "originStateProvinceCode",
			title: "Origin State",
			show: true,
			editable: false,
		},
		{
			field: "destinationCity",
			title: "Destination City",
			show: true,
			editable: false,
		},
		{
			field: "destinationStateProvinceCode",
			title: "Destination State",
			show: true,
			editable: false,
		},
		{
			field: "loadStatus",
			title: "Status",
			show: true,
			editable: false,
		},
		{
			field: "pickupApptDateTime",
			title: "Scheduled Pickup Date",
			show: false,
			filterCell: DateTextFilterCell,
			cells: { data: dateCell },
		},
		{
			field: "scheduledPickupTime",
			title: "Scheduled Pickup Time",
			show: false,
			minWidth: 200,
			cells: { data: timeCell },
		},
		{
			field: "actualPickupDateTime",
			title: "Actual Pickup Date",
			show: false,
			filterCell: DateTextFilterCell,
			cells: { data: dateCell },
		},
		{
			field: "actualDeliveryDateTime",
			title: "Actual Delivery Date",
			show: false,
			filterCell: DateTextFilterCell,
			cells: { data: dateCell },
		},
		{
			field: "originName",
			title: "Origin Name",
			show: false,
			editable: false,
		},
		{
			field: "originPostalCode",
			title: "Origin Postal Code",
			show: false,
			editable: false,
		},
		{
			field: "originCountryCode",
			title: "Origin Country",
			show: false,
			editable: false,
			cells: { data: countryCell },
		},
		{
			field: "destinationName",
			title: "Destination Name",
			show: false,
			editable: false,
		},
		{
			field: "destinationPostalCode",
			title: "Destination Postal Code",
			show: false,
			minWidth: 200,
			editable: false,
		},
		{
			field: "destinationCountryCode",
			title: "Destination Country",
			show: false,
			editable: false,
			cells: { data: countryCell },
		},
		{
			field: "trailerType",
			title: "Equipment Type",
			show: false,
			editable: false,
		},
		{
			field: "trailerSize",
			title: "Trailer Size",
			show: false,
			editable: false,
		},
		{
			field: "loadWeight",
			title: "Weight",
			show: false,
			editable: false,
		},
		{
			field: "palletCount",
			title: "Number of Handling Units",
			show: false,
			minWidth: 210,
			editable: false,
		},
		{
			field: "handlingUnits",
			title: "Handling Units",
			show: false,
			editable: false,
		},
		{
			field: "class",
			title: "Class",
			show: false,
			editable: false,
		},
		{
			field: "practicalMiles",
			title: "Practical Miles",
			show: false,
			editable: false,
		},
		{
			field: "totalBillAmount",
			title: "Rate",
			show: false,
			cells: { data: currencyCell },
		},
		{
			field: "numberOfStops",
			title: "Number of Stops",
			show: false,
			editable: false,
		},
		{
			field: "lane",
			title: "Lane",
			show: false,
			editable: false,
		},
		{
			field: "proNumber",
			title: "PRO #",
			show: false,
			editable: false,
		},
		{
			field: "soNumber",
			title: "SO #",
			show: false,
			editable: false,
		},
		{
			field: "containerNumber",
			title: "Container #",
			show: false,
			editable: false,
		},
	] satisfies BoardColumn[]
).map((col) => ({ minWidth: SHIPMENT_DETAIL_MIN_COLUMN_WIDTH, ...col }));
